// Training service for API calls
import axios from 'axios';
import {
  TrainingGuide,
  TrainingProgress,
  TrainingGuideListResponse,
  CreateTrainingGuideRequest,
  UpdateProgressRequest,
  TrainingFilters,
  QuizResult
} from '../types/training';

const API_BASE_URL = import.meta.env.VITE_API_URL;

class TrainingService {
  private getAuthHeaders() {
    // This will be implemented based on your auth system
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Get all training guides with optional filters
   */
  async getTrainingGuides(filters: TrainingFilters = {}, page = 1, limit = 10): Promise<TrainingGuideListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== undefined && value !== '')
      )
    });

    const response = await axios.get(`${API_BASE_URL}/training/guides?${params}`, {
      headers: this.getAuthHeaders()
    });

    return response.data;
  }

  /**
   * Get a specific training guide by ID
   */
  async getTrainingGuide(id: string): Promise<TrainingGuide> {
    const response = await axios.get(`${API_BASE_URL}/training/guides/${id}`, {
      headers: this.getAuthHeaders()
    });

    return response.data;
  }

  /**
   * Create a new training guide
   */
  async createTrainingGuide(data: CreateTrainingGuideRequest): Promise<TrainingGuide> {
    const response = await axios.post(`${API_BASE_URL}/training/guides`, data, {
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  }

  /**
   * Update a training guide
   */
  async updateTrainingGuide(id: string, data: Partial<CreateTrainingGuideRequest>): Promise<TrainingGuide> {
    const response = await axios.put(`${API_BASE_URL}/training/guides/${id}`, data, {
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  }

  /**
   * Delete a training guide
   */
  async deleteTrainingGuide(id: string): Promise<void> {
    await axios.delete(`${API_BASE_URL}/training/guides/${id}`, {
      headers: this.getAuthHeaders()
    });
  }

  /**
   * Get user's progress for a specific training guide
   */
  async getTrainingProgress(trainingGuideId: string): Promise<TrainingProgress> {
    const response = await axios.get(`${API_BASE_URL}/training/progress/${trainingGuideId}`, {
      headers: this.getAuthHeaders()
    });

    return response.data;
  }

  /**
   * Update user's training progress
   */
  async updateTrainingProgress(trainingGuideId: string, data: UpdateProgressRequest): Promise<TrainingProgress> {
    const response = await axios.put(`${API_BASE_URL}/training/progress/${trainingGuideId}`, data, {
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  }

  /**
   * Get training guides for a specific app
   */
  async getAppTrainingGuides(appId: string): Promise<TrainingGuide[]> {
    const response = await axios.get(`${API_BASE_URL}/apps/${appId}/training`, {
      headers: this.getAuthHeaders()
    });

    return response.data;
  }

  /**
   * Start a training guide (mark as in-progress)
   */
  async startTraining(trainingGuideId: string): Promise<TrainingProgress> {
    return this.updateTrainingProgress(trainingGuideId, {
      trainingGuideId,
      status: 'in-progress'
    });
  }

  /**
   * Complete a training section
   */
  async completeSection(trainingGuideId: string, sectionId: string, currentProgress: TrainingProgress): Promise<TrainingProgress> {
    const completedSections = [...currentProgress.completedSections];
    if (!completedSections.includes(sectionId)) {
      completedSections.push(sectionId);
    }

    return this.updateTrainingProgress(trainingGuideId, {
      trainingGuideId,
      status: 'in-progress',
      completedSections,
      currentSection: sectionId
    });
  }

  /**
   * Complete entire training guide
   */
  async completeTraining(trainingGuideId: string, rating?: number, feedback?: string): Promise<TrainingProgress> {
    return this.updateTrainingProgress(trainingGuideId, {
      trainingGuideId,
      status: 'completed',
      rating,
      feedback
    });
  }

  /**
   * Submit quiz results
   */
  async submitQuizResults(
    trainingGuideId: string, 
    sectionId: string, 
    results: QuizResult, 
    currentProgress: TrainingProgress
  ): Promise<TrainingProgress> {
    const quizResults = {
      ...currentProgress.quizResults,
      [sectionId]: results
    };

    return this.updateTrainingProgress(trainingGuideId, {
      trainingGuideId,
      quizResults
    });
  }

  /**
   * Get training statistics for a user
   */
  async getUserTrainingStats(): Promise<{
    totalGuides: number;
    completedGuides: number;
    inProgressGuides: number;
    totalTimeSpent: number;
    averageScore: number;
  }> {
    // This would need a separate endpoint on the backend
    // For now, we'll calculate client-side from available data
    try {
      const guides = await this.getTrainingGuides({}, 1, 1000); // Get all guides
      const progressPromises = guides.guides.map(guide => 
        this.getTrainingProgress(guide.id).catch(() => null)
      );
      
      const progressResults = await Promise.all(progressPromises);
      const validProgress = progressResults.filter(Boolean) as TrainingProgress[];

      const completedGuides = validProgress.filter(p => p.status === 'completed').length;
      const inProgressGuides = validProgress.filter(p => p.status === 'in-progress').length;
      const totalTimeSpent = validProgress.reduce((sum, p) => sum + (p.timeSpent || 0), 0);
      const scoresWithValues = validProgress.filter(p => p.score !== undefined);
      const averageScore = scoresWithValues.length > 0 
        ? scoresWithValues.reduce((sum, p) => sum + (p.score || 0), 0) / scoresWithValues.length 
        : 0;

      return {
        totalGuides: guides.total,
        completedGuides,
        inProgressGuides,
        totalTimeSpent,
        averageScore
      };
    } catch (error) {
      console.error('Error fetching training stats:', error);
      return {
        totalGuides: 0,
        completedGuides: 0,
        inProgressGuides: 0,
        totalTimeSpent: 0,
        averageScore: 0
      };
    }
  }
}

// Export singleton instance
export const trainingService = new TrainingService();
export default trainingService;